/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useStoreContext } from '@/contexts/StoreContext';

export const useProjectPath = () => {
  const { pathname } = useLocation();
  const { antflowServerConfig } = useStoreContext();
  const antflowServerPrefix = antflowServerConfig?.apiPrefix;

  const projectPath = useMemo(() => {
    if (!antflowServerPrefix) {
      return pathname;
    }
    return pathname.replace(antflowServerPrefix, '');
  }, [pathname, antflowServerPrefix]);

  const toBrowserProjectPath = useCallback(
    (url: string) => {
      const _url = new URL(url);
      const { pathname } = _url;
      return antflowServerPrefix ? pathname.replace(antflowServerPrefix, '') : pathname;
    },
    [antflowServerPrefix]
  );

  return { pathname, projectPath, antflowServerPrefix, toBrowserProjectPath };
};
